import React from 'react'
import { observer } from 'mobx-react-lite'
import { CheckCircle2, Loader2 } from 'lucide-react'
import { useTranslation } from '@/hooks/useTranslation'
import { PaywallPage, type PaywallPlan } from '@/components/PlanCard'
import { subscriptionStore } from './subscriptionStore'

/**
 * What a B2C user without an active subscription sees in place of the workspace: the
 * organisation's plans, and after a return from Stripe, what became of the payment.
 * The server refuses everything else for them anyway (SUBSCRIPTION_REQUIRED).
 */
const PlansScreen: React.FC = () => {
  const { t, currentLanguage } = useTranslation()
  const { plans, plansLoading, subscribable, subscribingPlanId, error, activation, mine, paywallContent } = subscriptionStore

  const errorMessage = error
    ? {
        PLAN_UNAVAILABLE: t('subscription.paywall.errors.planUnavailable'),
        SUBSCRIPTION_EXISTS: t('subscription.paywall.errors.subscriptionExists'),
        PAYMENTS_UNAVAILABLE: t('subscription.paywall.errors.paymentsUnavailable'),
        TOO_MANY_CHECKOUTS: t('subscription.paywall.errors.tooManyCheckouts'),
        CHECKOUT_BLOCKED: t('subscription.paywall.errors.checkoutBlocked'),
      }[error] || t('subscription.paywall.errors.checkoutFailed')
    : null

  if (activation === 'activating' || activation === 'stillPending') {
    const key = activation === 'activating' ? 'activating' : 'stillPending'
    return (
      <div className="flex flex-1 items-center justify-center p-6 font-[Inter]">
        <div className="max-w-md text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-[#E8F5EE] text-[#0F4C3A]">
            {activation === 'activating' ? <Loader2 className="h-6 w-6 animate-spin" /> : <CheckCircle2 className="h-6 w-6" />}
          </div>
          <h1 className="text-xl font-bold text-[#0F4C3A]">{t(`subscription.paywall.${key}.title`)}</h1>
          <p className="mt-2 text-sm text-[#4A5D52]">{t(`subscription.paywall.${key}.body`)}</p>
        </div>
      </div>
    )
  }

  // A subscription that existed and ended, as opposed to a first visit.
  const ended = !!mine?.subscription && ['canceled', 'unpaid', 'incomplete_expired'].includes(mine.subscription.status)

  // The server sends every offered plan with its card: a template's, or the default one.
  const offered: PaywallPlan[] = plans.filter((plan) => plan.card).map((plan) => ({ ...plan, card: plan.card! }))

  return (
    <div className="flex flex-1 justify-center overflow-y-auto bg-[#F7F9F6] px-4 py-8 md:px-8 md:py-12">
      <PaywallPage
        plans={offered}
        content={paywallContent}
        language={currentLanguage}
        notice={
          <>
            {ended && <p className="mt-3 text-sm font-medium text-[#0F4C3A]">{t('subscription.paywall.ended')}</p>}
            {activation === 'cancelled' && <p className="mt-3 text-sm text-[#4A5D52]">{t('subscription.paywall.cancelled')}</p>}
            {activation === 'error' && <p className="mt-3 text-sm text-[#B42318]">{t('subscription.paywall.failed')}</p>}
            {offered.length > 0 && !plansLoading && !subscribable && <p className="mt-3 text-sm text-[#4A5D52]">{t('subscription.paywall.unavailable')}</p>}
          </>
        }
        empty={
          plansLoading ? (
            <div className="mt-10 flex justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-[#0F4C3A]" />
            </div>
          ) : (
            <p className="mt-8 text-center text-sm text-[#4A5D52]">{t('subscription.paywall.unavailable')}</p>
          )
        }
        error={errorMessage}
        ctaFor={(plan) => ({
          disabled: !subscribable || subscribingPlanId !== null,
          loading: subscribingPlanId === plan.id,
          onClick: () => subscriptionStore.subscribe(plan.id),
        })}
      />
    </div>
  )
}

export default observer(PlansScreen)
